import React, { memo, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import { RECENTLY_VISITED_CLICK } from '../../globals/helpers/navigation-action';
import { readRecentlyVisited } from './storage';
import DataContainer from '../data';
import recentlyVisitedProductsQuery from './query.graphql';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import { ProductsSection } from '../products-section';
import { AppState } from 'src/reducers';
import { Product } from 'src/types';
import { productNavigation } from 'src/areas/product/actions/product';

const MAX_PRODUCTS_IN_GRID = 12;

const resolveProducts = (
  productIds: number[],
  productData: Product[]
): Product[] => {
  // For various reasons, some products may be missing from the data array,
  // e.g. if they've been removed since the user visited the product. We must
  // filter out those products and simply show as many recent productcs as
  // possible.
  const productById = productData.reduce(
    (map, product) => ({
      ...map,
      [product.id]: product,
    }),
    {}
  );

  return productIds.map(id => productById[id]).filter(Boolean);
};

type RecentlyVisitedProductsResponse = {
  data: {
    recentlyVisitedProducts: Product[];
  };
};

type RecentlyVisitedProductsProps = {
  offset?: number;
  landingPage?: boolean;
};

const RecentlyVisitedProducts: React.FC<RecentlyVisitedProductsProps> = props => {
  const { t } = useContext(AppContext);
  const dispatch = useDispatch();
  const tracking = useSelector((state: AppState) => state.areas.user.tracking);
  const { offset } = props;

  if (!tracking.settings.personalization) {
    // The user has disabled personalization altogether!
    return null;
  }

  const recentlyVisited = {
    products: readRecentlyVisited().products.slice(offset),
  };
  const productIds = recentlyVisited.products.map(p => p.id).filter(Boolean);

  if (
    !recentlyVisited ||
    !recentlyVisited.products ||
    recentlyVisited.products.length === 0
  ) {
    return null;
  }

  return (
    <DataContainer
      getKey={() => 'recentlyVisitedProducts'}
      variables={{ recentlyVisitedProductIds: productIds }}
      query={recentlyVisitedProductsQuery}
      render={({ data }: RecentlyVisitedProductsResponse) => {
        if (!data || !data.recentlyVisitedProducts) {
          return null;
        }

        const resolvedVisitedProducts = resolveProducts(
          productIds,
          data.recentlyVisitedProducts
        ).slice(0, MAX_PRODUCTS_IN_GRID);

        if (resolvedVisitedProducts.length < 4) {
          return null;
        }

        return (
          <ProductsSection
            products={resolvedVisitedProducts}
            title={t('landing', 'recently-visited-products.title')}
            onProductCardClick={({ index, link }) => {
              dispatch(productNavigation(RECENTLY_VISITED_CLICK, index + 1));
              trackInternalNavigationClick({
                scope:
                  InternalNavigationScope.RECENTLY_VISITED_PRODUCTS_PRODUCT_CARD,
                nextPage: link,
                index,
              });
            }}
            landingPage={props.landingPage}
          />
        );
      }}
    />
  );
};

export default memo(RecentlyVisitedProducts);
